import { ShieldCheck } from "lucide-react";
import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../api/axios';
import { Download, Filter, Users, GraduationCap, Building2, X, Award, BookOpen, Mail, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';

// --- İlerleme Çubuğu İçin Dinamik Renk Motoru ---
const getProgressTheme = (percent) => {
  const p = parseFloat(percent) || 0;
  if (p === 0) return { bg: '#e5e7eb', text: '#9ca3af' }; // %0 - Gri
  if (p < 50) return { bg: 'linear-gradient(90deg, #ff4b4b, #e30613)', text: '#e30613' }; // %1-%49 Kırmızı
  if (p < 80) return { bg: 'linear-gradient(90deg, #ffb700, #f57c00)', text: '#f57c00' }; // %50-%79 Turuncu
  if (p < 100) return { bg: 'linear-gradient(90deg, #4da1ff, #007aff)', text: '#007aff' }; // %80-%99 Mavi
  return { bg: 'linear-gradient(90deg, #34c759, #248a3d)', text: '#248a3d' }; // %100 Yeşil
};

const UserDetailModal = ({ user, onClose }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/user/' + user.id).then(r => { setCourses(r.data); setLoading(false); });
  }, [user.id]);

  // Sertifika İndirme Fonksiyonu
  const downloadCertificate = async (code, userName) => {
    try {
      const res = await api.get(`/certificates/download/${code}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safeName = userName ? userName.replace(/\s+/g, '-') : 'Sertifika';
      link.setAttribute('download', `${safeName}-${code}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      alert('Sertifika indirilirken hata oluştu. Sunucu yetkilendirmesini kontrol edin.');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container" style={{ maxWidth: 700, maxHeight: '85vh' }}>
        {/* Header */}
        <div className="modal-header d-flex align-center justify-between">
          <div className="d-flex align-center gap-3">
            <div className="avatar-circle-large">{user.name?.charAt(0).toUpperCase()}</div>
            <div>
              <div className="modal-title">{user.name}</div>
              <div className="modal-subtitle mt-1">{user.email} • {user.company} • {user.title}</div>
            </div>
          </div>
          <div className="d-flex align-center gap-3">
            <div className="stat-mini-box">
              <div className="stat-mini-val text-dark">{user.enrolled_courses || user.total_enrolled || 0}</div>
              <div className="stat-mini-label">KAYITLI</div>
            </div>
            <div className="stat-mini-box bg-green-light">
              <div className="stat-mini-val text-green">{user.completed_courses || user.total_completed || 0}</div>
              <div className="stat-mini-label text-green">TAMAMLANAN</div>
            </div>
            <button onClick={onClose} className="btn-close-icon ml-2">✕</button>
          </div>
        </div>

        {/* İçerik */}
        <div className="modal-body bg-gray-50" style={{ padding: '20px 28px' }}>
          {loading ? (
            <div className="empty-state">Yükleniyor...</div>
          ) : courses.length === 0 ? (
            <div className="empty-state">
              <BookOpen size={40} color="#cbd5e1" strokeWidth={1} style={{ margin: '0 auto 12px' }} />
              <div>Henüz hiçbir eğitime kayıtlı değil.</div>
            </div>
          ) : courses.map((c, i) => {
            const pTheme = getProgressTheme(c.progress_percent);
            return (
              <div key={i} className="course-progress-card">
                <div className="d-flex align-center justify-between mb-2">
                  <div>
                    <div className="font-medium text-dark">{c.title}</div>
                    <div className="text-small text-muted mt-1">
                      Kayıt: {new Date(c.enrolled_at).toLocaleDateString('tr-TR')}
                      {c.category && <span className="badge category-badge ml-2">{c.category}</span>}
                    </div>
                  </div>
                  <div>
                    {c.certificate_code ? (
                      <button
                        onClick={() => downloadCertificate(c.certificate_code, user.name)}
                        className="badge status-active d-flex align-center gap-1 cursor-pointer"
                        style={{ border: '1px solid #a7f3d0', padding: '6px 12px', background: '#ecfdf5', color: '#059669', transition: 'all 0.2s' }}
                        title="Tıklayıp Sertifikayı İndir"
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(5,150,105,0.1)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                      >
                        <Download size={13} /> Sertifika İndir
                      </button>
                    ) : (
                      <span className="badge status-warning">
                        Devam Ediyor
                      </span>
                    )}
                  </div>
                </div>
                <div className="d-flex align-center gap-3 mt-3">
                  <div className="progress-track flex-1">
                    <div
                      className="progress-fill"
                      style={{ width: (c.progress_percent || 0) + '%', background: pTheme.bg }}
                    />
                  </div>
                  <span className="text-small fw-bold min-w-40" style={{ color: pTheme.text }}>
                    %{c.progress_percent || 0}
                  </span>
                  <span className="text-small text-muted min-w-60 text-right">{c.completed_videos}/{c.total_videos} video</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function AdminReports() {
  const [activeTab, setActiveTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [selected, setSelected] = useState(() => localStorage.getItem('admin_report_selected_course') || '');
  const [report, setReport] = useState([]);
  const [userReport, setUserReport] = useState([]);
  const [companyReport, setCompanyReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCompany, setFilterCompany] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showReminderSuccessModal, setShowReminderSuccessModal] = useState(false);
  const [reminderSuccessMessage, setReminderSuccessMessage] = useState('');
  const [reminderSending, setReminderSending] = useState(false);

  const [userSearch, setUserSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterReportDept, setFilterReportDept] = useState('');
  const [filterReportUnit, setFilterReportUnit] = useState('');
  const [filterReportTitle, setFilterReportTitle] = useState('');

  const [reportPage, setReportPage] = useState(1);
  const reportPerPage = 50;

  const reportTotal = report.length;
  const reportCompleted = report.filter(r => parseInt(r.course_completed) === 1).length;
  const reportNotcompleted = report.filter(r => parseInt(r.course_completed) !== 1).length;
  const reportInprogress = report.filter(r => parseInt(r.course_completed) !== 1 && parseFloat(r.progress_percent) > 0).length;
  const reportNotstarted = report.filter(r => parseFloat(r.progress_percent) === 0).length;
  const reportRate = reportTotal > 0 ? Math.round(reportCompleted / reportTotal * 100) : 0;
  const rateTheme = getProgressTheme(reportRate);

  const reportDepartments = [...new Set(report.map(r => r.department).filter(Boolean))].sort();
  const reportUnits = [...new Set(report
    .filter(r => !filterReportDept || r.department === filterReportDept)
    .map(r => r.unit)
    .filter(Boolean)
  )].sort();
  const reportTitles = [...new Set(report
    .filter(r => (!filterReportDept || r.department === filterReportDept) &&
                 (!filterReportUnit || r.unit === filterReportUnit))
    .map(r => r.title)
    .filter(Boolean)
  )].sort();

  const filteredReport = report.filter(r => {
    if (filterStatus === 'completed' && parseInt(r.course_completed) !== 1) return false;
    if (filterStatus === 'notcompleted' && parseInt(r.course_completed) === 1) return false;
    if (filterStatus === 'inprogress' &&
        (parseInt(r.course_completed) === 1 || parseFloat(r.progress_percent) <= 0)) return false;
    if (filterStatus === 'notstarted' && parseFloat(r.progress_percent) !== 0) return false;
    if (filterReportDept && r.department !== filterReportDept) return false;
    if (filterReportUnit && r.unit !== filterReportUnit) return false;
    if (filterReportTitle && r.title !== filterReportTitle) return false;
    return true;
  });

  const pagedReport = filteredReport.slice((reportPage-1)*reportPerPage, reportPage*reportPerPage);
  const reportTotalPages = Math.ceil(filteredReport.length / reportPerPage);

  const [userPage, setUserPage] = useState(1);
  const [companyPage, setCompanyPage] = useState(1);
  const perPage = 50;

  const loadReport = useCallback(async (courseId) => {
    const targetId = courseId || selected;
    if (!targetId) return;
    setLoading(true);
    try {
      const r = await api.get('/reports/course/' + targetId);
      setReport(r.data);
      localStorage.setItem('admin_report_selected_course', targetId);
    } catch (err) {
      console.error('Rapor yüklenemedi:', err);
    }
    setLoading(false);
  }, [selected]);

  useEffect(() => {
    api.get('/courses').then(r => setCourses(r.data));
    api.get('/reports/users' + (selected ? '?course_id=' + selected : '')).then(r => setUserReport(r.data));
    api.get('/reports/by-company').then(r => setCompanyReport(r.data));
    
    if (selected) {
      loadReport(selected);
    }
  }, [selected, loadReport]);

  const companies = [...new Set(companyReport.map(u => u.company).filter(Boolean))].sort();
  const departments = [...new Set(companyReport.filter(u => !filterCompany || u.company === filterCompany).map(u => u.department).filter(Boolean))].sort();

  const filteredCompany = companyReport.filter(u => {
    if (filterCompany && u.company !== filterCompany) return false;
    if (filterDept && u.department !== filterDept) return false;
    return true;
  });

  // Türkçe karakter duyarlı arama normalizasyonu
  const normalizeTR = (text) => {
    if (!text) return "";
    return String(text)
      .toLocaleLowerCase("tr-TR")
      .replace(/İ/g, "i")
      .replace(/I/g, "ı");
  };

  const filteredUsers = userReport.filter(u => {
    if (!userSearch) return true;
    const term = normalizeTR(userSearch).trim();
    return (u.name && normalizeTR(u.name).includes(term)) ||
           (u.email && normalizeTR(u.email).includes(term)) ||
           (u.username && normalizeTR(u.username).includes(term));
  });

  const sendCourseReminder = async (userId) => {
    if (!selected || !userId) return;
    try {
      const r = await api.post(`/reports/course/${selected}/remind/${userId}`);
      setReminderSuccessMessage(r.data?.message || 'Hatırlatma maili başarıyla gönderildi.');
      setShowReminderSuccessModal(true);
    } catch (err) {
      setReminderSuccessMessage(err.response?.data?.error || 'Hatırlatma maili gönderilemedi.');
      setShowReminderSuccessModal(true);
    }
  };

  const sendAllCourseReminders = async () => {
    if (!selected) { alert('Önce bir eğitim seçin.'); return; }
    const notCompleted = report.filter(r => parseInt(r.course_completed) !== 1 && r.user_id);
    if (notCompleted.length === 0) { alert('Bu eğitimde tamamlamayan kullanıcı bulunmuyor.'); return; }
    setShowReminderModal(true);
  };

    const exportNotCompletedExcel = () => {
    if (!selected || report.length === 0) return;
    const notCompleted = report.filter(r => parseInt(r.course_completed) !== 1);
    const data = notCompleted.map(r => ({
      'SİCİL': r.username || '',
      'ADI SOYADI': r.name || '',
      'TELEFON': r.phone || '',
      'ŞİRKET': r.company || '',
      'MÜDÜRLÜK': r.department || '',
      'BİRİM': r.unit || '',
      'ÜNVAN': r.title || ''
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = [{ wch: 14 }, { wch: 30 }, { wch: 18 }, { wch: 25 }, { wch: 25 }, { wch: 25 }, { wch: 25 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Tamamlamayanlar');
    const courseName = courses.find(c => String(c.id) === String(selected))?.title || 'Eğitim';
    const safeName = courseName.replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '-');
    XLSX.writeFile(workbook, 'tamamlamayanlar-' + safeName + '.xlsx');
  };

  const exportCSV = (data, headers, filename) => {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const colWidths = headers.map((h, i) => {
      const maxLen = Math.max(h.length, ...data.map(r => String(r[i] || '').length));
      return { wch: Math.min(Math.max(maxLen + 2, 10), 50) };
    });
    ws['!cols'] = colWidths;
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cell_address]) continue;
        ws[cell_address].s = {
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          font: R === 0 ? { bold: true } : {}
        };
      }
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Rapor');
    XLSX.writeFile(wb, filename.replace('.csv', '.xlsx'));
  };

  const userTotalPages = Math.ceil(filteredUsers.length / perPage);
  const companyTotalPages = Math.ceil(filteredCompany.length / perPage);

  return (
    <AdminLayout>
      {/* Modals */}
      {showReminderSuccessModal && (
        <div className="modal-overlay" style={{ zIndex: 2100 }}>
          <div className="modal-container text-center" style={{ maxWidth: 400, padding: 32 }}>
            <div className="success-icon-large"><CheckCircle2 size={32} /></div>
            <h3 className="modal-title mb-2">Hatırlatma Maili</h3>
            <p className="text-muted mb-4">{reminderSuccessMessage}</p>
            <button onClick={() => setShowReminderSuccessModal(false)} className="btn-success w-full">Tamam</button>
          </div>
        </div>
      )}

      {showReminderModal && (
        <div className="modal-overlay" style={{ zIndex: 2000 }}>
          <div className="modal-container" style={{ maxWidth: 460 }}>
            <div className="modal-header d-flex align-center gap-3">
              <div className="avatar-circle-large bg-blue-light text-blue"><Mail size={22} /></div>
              <div>
                <h3 className="modal-title">Hatırlatma Maili</h3>
                <p className="modal-subtitle">Eğitim tamamlama hatırlatması</p>
              </div>
            </div>
            <div className="modal-body">
              <div className="info-box bg-gray-50 mb-4 text-center">
                <div className="text-small text-muted mb-1">Gönderilecek kişi sayısı</div>
                <div className="text-blue" style={{ fontSize: 32, fontWeight: 800 }}>
                  {report.filter(r => parseInt(r.course_completed) !== 1 && r.user_id).length} kişi
                </div>
              </div>
              <p className="text-muted text-center" style={{ lineHeight: 1.6 }}>
                Eğitimi henüz tamamlamayan çalışanlara sistem üzerinden otomatik eğitim tamamlama hatırlatma maili gönderilecektir. Onaylıyor musunuz?
              </p>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowReminderModal(false)} className="btn-cancel">İptal</button>
              <button
                onClick={async () => {
                  setReminderSending(true);
                  try {
                    const r = await api.post(`/reports/course/${selected}/remind-all`);
                    setShowReminderModal(false);
                    setReminderSuccessMessage(r.data?.message || 'Hatırlatma mailleri gönderildi.');
                    setShowReminderSuccessModal(true);
                  } catch (err) {
                    alert(err.response?.data?.error || 'Hatırlatma mailleri gönderilemedi.');
                  } finally {
                    setReminderSending(false);
                  }
                }}
                disabled={reminderSending}
                className="btn-primary"
              >
                <Mail size={16} /> {reminderSending ? 'Gönderiliyor...' : 'Mail Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedUser && <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />}

      <div className="page-container">

        {/* Üst Başlık ve Dışa Aktar */}
        <div className="page-header">
          <div>
            <h2 className="page-title">Raporlar ve Analizler</h2>
            <p className="page-subtitle">Platform kullanımını eğitim, kullanıcı ve şirket bazında inceleyin.</p>
          </div>
          <button onClick={() => {
            if (activeTab === 'courses') {
              const exportData = filteredReport.map(r => [
                r.username || '', r.name || '', r.phone || '', r.department || '', r.title || '',
                parseInt(r.course_completed) === 1 ? (r.certificate_date ? new Date(r.certificate_date).toLocaleDateString('tr-TR') : r.completed_at ? new Date(r.completed_at).toLocaleDateString('tr-TR') : 'Tamamlandı') : ''
              ]);
              exportCSV(exportData, ['Sicil', 'Ad Soyad', 'Telefon', 'Müdürlük', 'Ünvan', 'Tamamlanma Tarihi'], filterStatus === 'notcompleted' ? 'egitimi-tamamlamayanlar.csv' : 'egitim-raporu.csv');
            }
            if (activeTab === 'users') exportCSV(filteredUsers.map(r => [r.username||'', r.name, r.phone||'', r.department||'', r.title||'', r.total_enrolled, r.total_completed, r.last_activity ? new Date(r.last_activity).toLocaleDateString('tr-TR') : '']), ['Sicil','Ad Soyad','Telefon','Müdürlük','Ünvan','Kayıtlı','Tamamlanan','Son Aktivite'], 'kullanici-raporu.csv');
            if (activeTab === 'company') exportCSV(filteredCompany.map(r => [r.username||'', r.name, r.phone||'', r.company, r.department, r.unit, r.title, r.enrolled_courses, r.completed_courses]), ['Sicil','Ad Soyad','Telefon','Şirket','Müdürlük','Birim','Ünvan','Kayıtlı','Tamamlanan'], 'sirket-raporu.csv');
          }} className="btn-success">
            <Download size={16} /> Dışa Aktar
          </button>
        </div>

        {/* Modern Tabs */}
        <div className="tabs-container">
          {[['courses', GraduationCap, 'Eğitim Bazlı'], ['users', Users, 'Kullanıcı Bazlı'], ['company', Building2, 'Şirket Bazlı']].map(([key, Icon, label]) => (
            <button key={key} onClick={() => setActiveTab(key)} className={`tab-btn ${activeTab === key ? 'active' : ''}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {/* EĞİTİM BAZLI RAPOR */}
        {activeTab === 'courses' && (
          <div className="tab-content-anim">
            {/* Filtre Bar */}
            <div className="filter-card">
              <div className="d-flex align-end gap-3 flex-wrap w-full">
                <div className="flex-1 min-w-200">
                  <label className="form-label">EĞİTİM SEÇ</label>
                  <select value={selected} onChange={e => setSelected(e.target.value)} className="form-input bg-white">
                    <option value="">Eğitim seçin...</option>
                    {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                </div>
                
                <button onClick={() => loadReport()} disabled={!selected || loading} className="btn-outline-refresh" title="Verileri Güncelle">
                  <RefreshCw size={15} className={loading ? 'fa-spin' : ''} /> {loading ? 'Güncelleniyor...' : 'Yenile'}
                </button>

                <button onClick={sendAllCourseReminders} disabled={!selected || report.length === 0} className="btn-outline-orange">
                  <Mail size={15} /> Hatırlatma Gönder
                </button>
                <button onClick={exportNotCompletedExcel} disabled={!selected || report.length === 0} className="btn-outline-green">
                  <Download size={15} /> Tamamlamayanları İndir
                </button>
              </div>
            </div>

            {loading && <div className="empty-state py-8">Yükleniyor...</div>}

            {report.length > 0 && (
              <>
                {/* İstatistik Dashboard Grid */}
                <div className="stats-grid mb-4">
                  {[
                    { label: 'Toplam Kayıtlı', value: reportTotal, colorClass: 'text-dark', bgClass: 'bg-gray-100', status: 'all' },
                    { label: 'Tamamlandı', value: reportCompleted, colorClass: 'text-green', bgClass: 'bg-green-light', status: 'completed' },
                    { label: 'Henüz Tamamlamadı', value: reportNotcompleted, colorClass: 'text-red', bgClass: 'bg-red-light', status: 'notcompleted' },
                    { label: 'Devam Ediyor', value: reportInprogress, colorClass: 'text-orange', bgClass: 'bg-orange-light', status: 'inprogress' },
                    { label: 'Başlamadı', value: reportNotstarted, colorClass: 'text-muted', bgClass: 'bg-gray-50', status: 'notstarted' },
                  ].map(({ label, value, colorClass, bgClass, status }) => (
                    <div key={label} onClick={() => { if(status) { setFilterStatus(status); setReportPage(1); } }}
                         className={`stat-box cursor-pointer ${filterStatus === status ? 'ring-active' : ''} ${bgClass}`}>
                      <div className={`stat-num ${colorClass}`}>{value}</div>
                      <div className="stat-text">{label}</div>
                    </div>
                  ))}
                </div>

                {/* Tamamlanma Oranı Bar */}
                <div className="content-card p-4 mb-4">
                  <div className="d-flex justify-between mb-2">
                    <span className="font-medium text-dark">Genel Tamamlanma Oranı</span>
                    <span className="fw-bold" style={{ color: rateTheme.text }}>{reportRate}%</span>
                  </div>
                  <div className="progress-track-large">
                    <div className="progress-fill-large" style={{ width: reportRate + '%', background: rateTheme.bg }} />
                  </div>
                </div>

                {/* Ek Filtre Bar */}
                <div className="content-card p-3 mb-4 d-flex align-center gap-3 flex-wrap bg-gray-50">
                  <select
                    value={filterReportDept}
                    onChange={e => {
                      setFilterReportDept(e.target.value);
                      setFilterReportUnit('');
                      setFilterReportTitle('');
                      setReportPage(1);
                    }}
                    className="form-input min-w-200 flex-1"
                  >
                    <option value="">Tüm Müdürlükler</option>
                    {reportDepartments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>

                  <select
                    value={filterReportUnit}
                    onChange={e => {
                      setFilterReportUnit(e.target.value);
                      setFilterReportTitle('');
                      setReportPage(1);
                    }}
                    className="form-input min-w-200 flex-1"
                  >
                    <option value="">Tüm Birimler</option>
                    {reportUnits.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>

                  <select
                    value={filterReportTitle}
                    onChange={e => {
                      setFilterReportTitle(e.target.value);
                      setReportPage(1);
                    }}
                    className="form-input min-w-200 flex-1"
                  >
                    <option value="">Tüm Ünvanlar</option>
                    {reportTitles.map(title => (
                      <option key={title} value={title}>{title}</option>
                    ))}
                  </select>

                  {(filterReportDept || filterReportUnit || filterReportTitle) && (
                    <button
                      onClick={() => {
                        setFilterReportDept('');
                        setFilterReportUnit('');
                        setFilterReportTitle('');
                        setReportPage(1);
                      }}
                      className="btn-cancel"
                      style={{ padding: '10px 14px' }}
                    >
                      Filtreleri Temizle
                    </button>
                  )}
                </div>

                {/* Tablo Kartı */}
                <div className="content-card">
                  <div className="table-header d-flex justify-between align-center flex-wrap gap-3">
                    <div className="segmented-control">
                      {[['all','Tümü'],['completed','Tamamlandı'],['notcompleted','Tamamlamayan'],['inprogress','Devam Eden'],['notstarted','Başlamayan']].map(([val, label]) => (
                        <button key={val} onClick={() => { setFilterStatus(val); setReportPage(1); }} className={`segment-btn ${filterStatus === val ? 'active' : ''}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                    <span className="text-small text-muted">{filteredReport.length} kayıt listeleniyor</span>
                  </div>
                  <div className="table-responsive">
                    <table className="modern-table">
                      <thead>
                        <tr>
                          {['KULLANICI', 'EMAIL', 'MÜDÜRLÜK', 'BİRİM', 'ÜNVAN', 'KAYIT TARİHİ', 'TAMAMLANMA', 'DURUM', 'İLERLEME', 'SERTİFİKA'].map(h => <th key={h}>{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {pagedReport.map((r, i) => {
                          const rTheme = getProgressTheme(r.progress_percent);
                          return (
                            <tr key={i}>
                              <td className="font-medium text-dark">{r.name}</td>
                              <td className="text-muted">{r.email || '—'}</td>
                              <td className="text-muted">{r.department || '—'}</td>
                              <td className="text-muted">{r.unit || '—'}</td>
                              <td className="text-muted">{r.title || '—'}</td>
                              <td className="text-muted">{new Date(r.enrolled_at).toLocaleDateString('tr-TR')}</td>
                              <td className="text-muted">{r.certificate_date ? new Date(r.certificate_date).toLocaleDateString('tr-TR') : '—'}</td>
                              <td>
                                {parseInt(r.course_completed) === 1
                                  ? <span className="badge status-active">Tamamlandı</span>
                                  : <span className="badge status-warning">Devam Ediyor</span>}
                              </td>
                              <td style={{ minWidth: 150 }}>
                                <div className="d-flex align-center gap-2">
                                  <div className="progress-track flex-1">
                                    <div className="progress-fill" style={{ width: (r.progress_percent || 0) + '%', background: rTheme.bg }} />
                                  </div>
                                  <span className="text-small fw-bold min-w-40" style={{ color: rTheme.text }}>%{r.progress_percent || 0}</span>
                                </div>
                              </td>
                              <td>
                                {r.certificate_code ? <span className="badge category-badge d-flex align-center gap-1 w-fit"><Award size={12}/> Var</span> : <span className="text-muted">—</span>}
                              </td>
                            </tr>
                          );
                        })}
                        {pagedReport.length === 0 && <tr><td colSpan="10" className="empty-state">Bu filtreye uygun kayıt bulunamadı.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  {/* Sayfalama */}
                  {reportTotalPages > 1 && (
                    <div className="pagination-container">
                      <button onClick={() => setReportPage(p => Math.max(1, p-1))} disabled={reportPage===1} className="btn-pagination">← Önceki</button>
                      <span className="pagination-info">{reportPage} / {reportTotalPages}</span>
                      <button onClick={() => setReportPage(p => Math.min(reportTotalPages, p+1))} disabled={reportPage===reportTotalPages} className="btn-pagination">Sonraki →</button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* KULLANICI BAZLI RAPOR */}
        {activeTab === 'users' && (
          <div className="tab-content-anim">
            <div className="content-card">
              <div className="search-bar-container">
                <div className="search-input-wrapper flex-1 max-w-400">
                  <input placeholder="Kullanıcı adı, sicil veya email ara..." value={userSearch} onChange={e => { setUserSearch(e.target.value); setUserPage(1); }} className="search-input pl-3" />
                </div>
                <div className="result-count">Toplam <strong>{filteredUsers.length}</strong> kullanıcı</div>
              </div>
              <div className="table-responsive">
                <table className="modern-table">
                  <thead>
                    <tr>
                      {['KULLANICI', 'EMAIL', 'KAYITLI EĞİTİM', 'TAMAMLANAN', 'SON AKTİVİTE'].map(h => <th key={h} className={h.includes('EĞİTİM') || h.includes('TAMAMLANAN') ? 'text-center' : ''}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.slice((userPage-1)*perPage, userPage*perPage).map((u, i) => (
                      <tr key={i} className="cursor-pointer" onClick={() => setSelectedUser(u)}>
                        <td className="font-medium text-dark d-flex align-center gap-2">
                          <div className="avatar-circle">{u.name?.charAt(0).toUpperCase()}</div>
                          <div>
                            <div>{u.name}</div>
                            {u.username && (
                              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal' }}>
                                Sicil: {u.username}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="text-muted">{u.email || '—'}</td>
                        <td className="text-center"><span className="badge bg-gray-100 text-dark px-3">{u.total_enrolled}</span></td>
                        <td className="text-center"><span className="badge bg-green-light text-green px-3">{u.total_completed}</span></td>
                        <td className="text-muted">{u.last_activity ? new Date(u.last_activity).toLocaleDateString('tr-TR') : '—'}</td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && <tr><td colSpan="5" className="empty-state">Kullanıcı bulunamadı.</td></tr>}
                  </tbody>
                </table>
              </div>
              {/* Sayfalama */}
              {userTotalPages > 1 && (
                <div className="pagination-container">
                  <button onClick={() => setUserPage(p => Math.max(1, p-1))} disabled={userPage===1} className="btn-pagination">← Önceki</button>
                  <span className="pagination-info">{userPage} / {userTotalPages}</span>
                  <button onClick={() => setUserPage(p => Math.min(userTotalPages, p+1))} disabled={userPage===userTotalPages} className="btn-pagination">Sonraki →</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ŞİRKET BAZLI RAPOR */}
        {activeTab === 'company' && (
          <div className="tab-content-anim">
            <div className="filter-card mb-4">
              <div className="d-flex align-end gap-3 flex-wrap">
                <div className="flex-1 min-w-200">
                  <label className="form-label">ŞİRKET</label>
                  <select value={filterCompany} onChange={e => { setFilterCompany(e.target.value); setFilterDept(''); setCompanyPage(1); }} className="form-input bg-white">
                    <option value="">Tüm Şirketler</option>
                    {companies.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="flex-1 min-w-200">
                  <label className="form-label">MÜDÜRLÜK</label>
                  <select value={filterDept} onChange={e => { setFilterDept(e.target.value); setCompanyPage(1); }} className="form-input bg-white">
                    <option value="">Tüm Müdürlükler</option>
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="content-card">
              <div className="table-responsive">
                <table className="modern-table">
                  <thead>
                    <tr>
                      {['AD SOYAD', 'EMAIL', 'ŞİRKET', 'MÜDÜRLÜK', 'BİRİM', 'UNVAN', 'KAYITLI', 'TAMAMLANAN'].map(h => <th key={h} className={h === 'KAYITLI' || h === 'TAMAMLANAN' ? 'text-center' : ''}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompany.slice((companyPage-1)*perPage, companyPage*perPage).map((u, i) => (
                      <tr key={i} className="cursor-pointer" onClick={() => setSelectedUser(u)}>
                        <td className="font-medium text-dark">{u.name}</td>
                        <td className="text-muted">{u.email || '—'}</td>
                        <td>{u.company && <span className="badge category-badge">{u.company}</span>}</td>
                        <td className="text-muted text-truncate max-w-150" title={u.department}>{u.department || '—'}</td>
                        <td className="text-muted text-truncate max-w-150" title={u.unit}>{u.unit || '—'}</td>
                        <td>{u.title && <span className="badge title-badge">{u.title}</span>}</td>
                        <td className="text-center"><span className="badge bg-gray-100 text-dark px-3">{u.enrolled_courses}</span></td>
                        <td className="text-center"><span className="badge bg-green-light text-green px-3">{u.completed_courses}</span></td>
                      </tr>
                    ))}
                    {filteredCompany.length === 0 && <tr><td colSpan="8" className="empty-state">Kayıt bulunamadı.</td></tr>}
                  </tbody>
                </table>
              </div>
              {/* Sayfalama */}
              {companyTotalPages > 1 && (
                <div className="pagination-container">
                  <button onClick={() => setCompanyPage(p => Math.max(1, p-1))} disabled={companyPage===1} className="btn-pagination">← Önceki</button>
                  <span className="pagination-info">{companyPage} / {companyTotalPages}</span>
                  <button onClick={() => setCompanyPage(p => Math.min(companyTotalPages, p+1))} disabled={companyPage===companyTotalPages} className="btn-pagination">Sonraki →</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .page-container { animation: fadeSlideUp 0.4s ease-out; }
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .tab-content-anim { animation: fadeIn 0.3s ease-out; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

        /* Typography & Utils */
        .text-muted { color: #6b7280; }
        .text-dark { color: #111827; }
        .font-medium { font-weight: 500; }
        .fw-bold { font-weight: 700; }
        .fw-normal { font-weight: 400; }
        .text-small { font-size: 12.5px; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .w-full { width: 100%; }
        .w-fit { width: fit-content; }
        .flex-1 { flex: 1; }
        .d-flex { display: flex; }
        .align-center { align-items: center; }
        .align-end { align-items: flex-end; }
        .justify-between { justify-content: space-between; }
        .justify-center { justify-content: center; }
        .justify-end { justify-content: flex-end; }
        .gap-1 { gap: 4px; }
        .gap-2 { gap: 8px; }
        .gap-3 { gap: 12px; }
        .flex-wrap { flex-wrap: wrap; }
        .cursor-pointer { cursor: pointer; }

        .mb-1 { margin-bottom: 4px; }
        .mb-2 { margin-bottom: 8px; }
        .mb-3 { margin-bottom: 12px; }
        .mb-4 { margin-bottom: 16px; }
        .mt-1 { margin-top: 4px; }
        .mt-2 { margin-top: 8px; }
        .mt-3 { margin-top: 12px; }
        .mt-4 { margin-top: 16px; }
        .ml-2 { margin-left: 8px; }
        .p-3 { padding: 12px; }
        .p-4 { padding: 16px; }
        .py-8 { padding-top: 32px; padding-bottom: 32px; }
        .px-3 { padding-left: 12px; padding-right: 12px; }

        .min-w-40 { min-width: 40px; }
        .min-w-60 { min-width: 60px; }
        .min-w-200 { min-width: 200px; }
        .max-w-150 { max-width: 150px; }
        .max-w-400 { max-width: 400px; }
        .text-truncate { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

        /* Colors */
        .text-red { color: #e30613; }
        .text-green { color: #16a34a; }
        .text-orange { color: #ea580c; }
        .text-blue { color: #2563eb; }

        .bg-white { background: #ffffff; }
        .bg-gray-50 { background: #f9fafb; }
        .bg-gray-100 { background: #f3f4f6; }
        .bg-red { background: #e30613; }
        .bg-red-light { background: #fef2f2; }
        .bg-green { background: #16a34a; }
        .bg-green-light { background: #f0fdf4; }
        .bg-orange { background: #ea580c; }
        .bg-orange-light { background: #fff7ed; }
        .bg-blue-light { background: #eff6ff; }

        /* Header */
        .page-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; flex-wrap: wrap; gap: 16px; }
        .page-title { margin: 0 0 4px 0; font-size: 22px; font-weight: 700; color: #111827; letter-spacing: -0.5px; }
        .page-subtitle { margin: 0; font-size: 13.5px; color: #6b7280; }

        /* Tabs */
        .tabs-container { display: inline-flex; background: #f3f4f6; border-radius: 12px; padding: 4px; margin-bottom: 24px; }
        .tab-btn { padding: 8px 20px; border: none; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 13.5px; display: flex; align-items: center; gap: 8px; background: transparent; color: #6b7280; transition: all 0.2s; }
        .tab-btn:hover:not(.active) { color: #111827; }
        .tab-btn.active { background: #ffffff; color: #e30613; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }

        /* Cards & Containers */
        .filter-card { background: #ffffff; border-radius: 16px; border: 1px solid #eaecf0; box-shadow: 0 4px 6px -1px rgba(16, 24, 40, 0.03); padding: 20px; margin-bottom: 20px; }
        .content-card { background: #ffffff; border-radius: 16px; border: 1px solid #eaecf0; box-shadow: 0 4px 6px -1px rgba(16, 24, 40, 0.03); overflow: hidden; }
        .info-box { padding: 16px; border-radius: 12px; border: 1px solid #e5e7eb; }

        /* Form Inputs */
        .form-label { display: block; margin-bottom: 6px; font-weight: 600; font-size: 12px; color: #475467; text-transform: uppercase; letter-spacing: 0.5px;}
        .form-input { width: 100%; padding: 10px 14px; border: 1px solid #d0d5dd; border-radius: 10px; font-size: 14px; outline: none; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; box-sizing: border-box;}
        .form-input:focus { border-color: #e30613; box-shadow: 0 0 0 4px #fff1f2; }

        .search-bar-container { padding: 16px 20px; border-bottom: 1px solid #eaecf0; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; background: #fcfcfd; }
        .search-input { width: 100%; padding: 10px 14px; border: 1px solid #d0d5dd; border-radius: 10px; font-size: 14px; outline: none; transition: all 0.2s; }
        .search-input:focus { border-color: #e30613; box-shadow: 0 0 0 4px #fff1f2; }
        .result-count { font-size: 13px; color: #6b7280; }

        /* Buttons */
        .btn-primary { background: #e30613; color: white; border: 1px solid #e30613; border-radius: 10px; padding: 10px 20px; cursor: pointer; font-weight: 600; font-size: 13.5px; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; white-space: nowrap; }
        .btn-primary:hover:not(:disabled) { background: #ba000a; border-color: #ba000a; transform: translateY(-1px); box-shadow: 0 4px 6px rgba(227, 6, 19, 0.2); }
        .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

        .btn-outline-refresh { background: #f8fafc; color: #334155; border: 1px solid #cbd5e1; border-radius: 10px; padding: 10px 18px; cursor: pointer; font-weight: 600; font-size: 13.5px; display: flex; align-items: center; justify-content: center; gap: 6px; transition: all 0.2s; white-space: nowrap; }
        .btn-outline-refresh:hover:not(:disabled) { background: #f1f5f9; border-color: #94a3b8; color: #0f172a; }
        .btn-outline-refresh:disabled { opacity: 0.6; cursor: not-allowed; }

        .btn-success { background: #16a34a; color: white; border: 1px solid #16a34a; border-radius: 10px; padding: 10px 20px; cursor: pointer; font-weight: 600; font-size: 13.5px; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; white-space: nowrap; }
        .btn-success:hover:not(:disabled) { background: #15803d; border-color: #15803d; transform: translateY(-1px); }

        .btn-outline-green { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; border-radius: 10px; padding: 10px 18px; cursor: pointer; font-weight: 600; font-size: 13.5px; display: flex; align-items: center; justify-content: center; gap: 6px; transition: all 0.2s; white-space: nowrap; }
        .btn-outline-green:hover:not(:disabled) { background: #dcfce7; border-color: #86efac; }
        .btn-outline-green:disabled { background: #f9fafb; color: #9ca3af; border-color: #e5e7eb; cursor: not-allowed; }

        .btn-outline-orange { background: #fff7ed; color: #ea580c; border: 1px solid #fed7aa; border-radius: 10px; padding: 10px 18px; cursor: pointer; font-weight: 600; font-size: 13.5px; display: flex; align-items: center; justify-content: center; gap: 6px; transition: all 0.2s; white-space: nowrap; }
        .btn-outline-orange:hover:not(:disabled) { background: #ffedd5; border-color: #fdba74; }
        .btn-outline-orange:disabled { background: #f9fafb; color: #9ca3af; border-color: #e5e7eb; cursor: not-allowed; }

        .btn-cancel { padding: 10px 20px; border: 1px solid #d0d5dd; background: #ffffff; border-radius: 10px; cursor: pointer; font-weight: 600; font-size: 14px; color: #344054; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.2s; }
        .btn-cancel:hover { background: #f9fafb; border-color: #98a2b3; }
        .btn-close-icon { background: #f3f4f6; border: none; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; color: #6b7280; font-size: 16px; cursor: pointer; transition: all 0.2s; }
        .btn-close-icon:hover { background: #e5e7eb; color: #111827; }

        /* Segmented Control */
        .segmented-control { display: inline-flex; background: #f3f4f6; border-radius: 10px; padding: 3px; }
        .segment-btn { padding: 6px 14px; border: none; border-radius: 8px; cursor: pointer; font-size: 12px; font-weight: 600; background: transparent; color: #6b7280; transition: all 0.2s; }
        .segment-btn.active { background: #ffffff; color: #e30613; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }

        /* Stat Grid */
        .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 12px; }
        .stat-box { padding: 16px; border-radius: 16px; text-align: center; border: 2px solid transparent; transition: all 0.2s; }
        .stat-box:hover { transform: translateY(-2px); }
        .stat-box.ring-active { border-color: currentColor; }
        .stat-num { font-size: 26px; font-weight: 800; line-height: 1.2; margin-bottom: 4px; }
        .stat-text { font-size: 12px; font-weight: 600; color: #6b7280; }

        .stat-mini-box { text-align: center; padding: 8px 16px; border-radius: 12px; background: #f3f4f6; }
        .stat-mini-val { font-size: 20px; font-weight: 800; line-height: 1; margin-bottom: 4px; }
        .stat-mini-label { font-size: 10px; font-weight: 700; color: #6b7280; letter-spacing: 0.5px; }

        /* Progress Bars */
        .progress-track { height: 6px; background: #e5e7eb; border-radius: 4px; overflow: hidden; width: 100%; }
        .progress-fill { height: 100%; border-radius: 4px; transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1); }
        .progress-track-large { height: 10px; background: #f3f4f6; border-radius: 6px; overflow: hidden; width: 100%; }
        .progress-fill-large { height: 100%; border-radius: 6px; transition: width 1s cubic-bezier(0.4, 0, 0.2, 1); }

        /* Table */
        .table-responsive { overflow-x: auto; }
        .table-header { padding: 16px 20px; border-bottom: 1px solid #eaecf0; }
        .modern-table { width: 100%; border-collapse: collapse; min-width: 900px; }
        .modern-table th { padding: 14px 20px; text-align: left; font-size: 11px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; background: #f9fafb; border-bottom: 1px solid #eaecf0; }
        .modern-table td { padding: 14px 20px; border-bottom: 1px solid #eaecf0; vertical-align: middle; transition: background 0.2s; font-size: 13.5px;}
        .modern-table tr:hover td { background: #f9fafb; }
        .modern-table tr:last-child td { border-bottom: none; }

        /* Badges */
        .badge { display: inline-flex; align-items: center; padding: 4px 10px; border-radius: 20px; font-size: 11.5px; font-weight: 600; white-space: nowrap;}
        .category-badge { background: #f3f4f6; color: #4b5563; border: 1px solid #e5e7eb; }
        .title-badge { background: #f8fafc; color: #475569; border: 1px solid #f1f5f9; }
        .status-active { background: #ecfdf5; color: #059669; border: 1px solid #a7f3d0; }
        .status-warning { background: #fff7ed; color: #ea580c; border: 1px solid #fed7aa; }
        .status-passive { background: #f3f4f6; color: #6b7280; border: 1px solid #e5e7eb; }

        /* Avatars */
        .avatar-circle { width: 32px; height: 32px; border-radius: 10px; background: #fff1f2; color: #e30613; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 14px; flex-shrink: 0; }
        .avatar-circle-large { width: 48px; height: 48px; border-radius: 14px; background: #fff1f2; color: #e30613; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 20px; flex-shrink: 0; }

        /* Modal */
        .modal-overlay { position: fixed; inset: 0; background: rgba(16, 24, 40, 0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
        .modal-container { background: #ffffff; border-radius: 20px; width: 100%; box-shadow: 0 20px 24px -4px rgba(16, 24, 40, 0.08); animation: modalPop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); display: flex; flex-direction: column; overflow: hidden;}
        @keyframes modalPop { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        .modal-header { padding: 24px 28px; border-bottom: 1px solid #eaecf0; }
        .modal-title { margin: 0; font-size: 19px; font-weight: 700; color: #111827; }
        .modal-subtitle { margin: 0; font-size: 13.5px; color: #6b7280; }
        .modal-body { padding: 24px 28px; overflow-y: auto;}
        .modal-footer { padding: 16px 28px; background: #f9fafb; border-top: 1px solid #eaecf0; display: flex; justify-content: flex-end; gap: 12px; }

        .success-icon-large { width: 64px; height: 64px; border-radius: 50%; background: #ecfdf5; color: #10b981; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
        .course-progress-card { background: #ffffff; border: 1px solid #eaecf0; border-radius: 14px; padding: 16px 20px; margin-bottom: 12px; box-shadow: 0 2px 4px rgba(0,0,0,0.02); transition: transform 0.2s; }
        .course-progress-card:hover { transform: translateY(-2px); border-color: #d0d5dd; }

        /* Pagination */
        .pagination-container { padding: 16px 20px; border-top: 1px solid #eaecf0; display: flex; align-items: center; gap: 12px; justify-content: center; background: #fcfcfd; }
        .btn-pagination { padding: 8px 16px; border: 1px solid #d0d5dd; border-radius: 8px; background: white; cursor: pointer; font-size: 13px; font-weight: 600; color: #344054; transition: all 0.2s; box-shadow: 0 1px 2px rgba(16,24,40,0.05); }
        .btn-pagination:hover:not(:disabled) { background: #f9fafb; border-color: #9ca3af; }
        .btn-pagination:disabled { opacity: 0.5; cursor: not-allowed; }
        .pagination-info { font-size: 13.5px; font-weight: 600; color: #475467; }

        .empty-state { text-align: center; padding: 40px !important; color: #6b7280; font-size: 14px; font-style: italic; }
      `}</style>
    </AdminLayout>
  );
}
